import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { Button, Card, Col, Container, Input, Row, Spinner } from "reactstrap";
import dayjs from "dayjs";
import { getDashboardOperador } from "../../../helpers/backend_helper";
import { ESTADOS } from "../SalidasStatus/statusHelper";
import { CHIPS, esHoy, fechaLarga, filtrar, pickSalidaEnCurso } from "./operadorHelper";
import SalidaEnCursoCard from "./SalidaEnCursoCard";
import SalidaTile from "./SalidaTile";
import "../SalidasStatus/SalidasStatus.css";
import "./OperadorDashboard.css";

const HOY = () => dayjs().format("YYYY-MM-DD");

/*
  Tablero del operador: sus salidas del dia. Consume dashboard/operador, que resuelve el
  operador desde la sesion, asi que esta pantalla nunca manda un operator_id.
*/
const OperadorDashboard = () => {
  document.title = "Mis salidas | Sistema de Logística";

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [stale, setStale] = useState(false);
  const [data, setData] = useState({ operador: null, resumen: {}, salidas: [], today: null });
  const [fecha, setFecha] = useState(HOY());
  const [filtro, setFiltro] = useState("todas");
  const history = useHistory();

  const abrir = (id) => history.push(`/dashboard-operador/salida/${id}`);

  const getData = useCallback(async (dia) => {
    setLoading(true);
    try {
      const response = await getDashboardOperador({ withToast: false, params: { fecha: dia } });
      if (response?.status) {
        setData({
          operador: response.operador ?? null,
          resumen: response.resumen ?? {},
          salidas: response.salidas ?? [],
          today: response.today ?? dayjs().format("YYYY-MM-DD HH:mm:ss"),
        });
        setStale(false);
      } else {
        setStale(true);
      }
    } catch (error) {
      console.error(error);
      setStale(true);
    }
    setLoading(false);
    setInitialized(true);
  }, []);

  // El refresco automatico solo tiene sentido sobre el dia en curso
  useEffect(() => {
    getData(fecha);
    if (!esHoy(fecha)) return undefined;

    const interval = setInterval(() => getData(fecha), 1000 * 60 * 1);
    return () => clearInterval(interval);
  }, [getData, fecha]);

  const salidas = data.salidas;
  const destacada = useMemo(() => pickSalidaEnCurso(salidas), [salidas]);
  const visibles = useMemo(() => filtrar(salidas, filtro), [salidas, filtro]);

  const enVivo = esHoy(fecha) && !stale;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <Card className="salidas-status-card operador-card">
                <div className="salidas-status-header">
                  <div>
                    {/* La fecha larga es la del día consultado, no la del servidor: el corte va en la píldora */}
                    <div style={{ fontSize: "12px", color: "#777a89" }}>{fechaLarga(fecha)}</div>
                    <div style={{ fontSize: "20px", fontWeight: 700, lineHeight: 1.2 }}>
                      {data.operador ? `Hola, ${data.operador.primer_nombre}` : "Mis salidas del día"}
                    </div>
                  </div>

                  {esHoy(fecha) && (
                    <div
                      className="d-flex align-items-center"
                      style={{
                        gap: 7,
                        padding: "5px 11px 5px 9px",
                        background: enVivo ? ESTADOS.tie.bg : ESTADOS.rie.bg,
                        border: `1px solid ${enVivo ? ESTADOS.tie.bd : ESTADOS.rie.bd}`,
                        borderRadius: 100,
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: enVivo ? ESTADOS.tie.color : ESTADOS.rie.color,
                        }}
                      />
                      <span style={{ fontSize: "11px", fontWeight: 600, color: enVivo ? ESTADOS.tie.ink : ESTADOS.rie.ink }}>
                        {enVivo ? "En vivo" : "Sin actualizar"}
                      </span>
                      {data.today && (
                        <span style={{ fontSize: "11px", color: enVivo ? ESTADOS.tie.ink : ESTADOS.rie.ink }}>
                          · corte {dayjs(data.today).format("HH:mm:ss")}
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ flex: 1 }} />

                  <Input
                    type="date"
                    bsSize="sm"
                    style={{ width: 160 }}
                    value={fecha}
                    max={HOY()}
                    onChange={(e) => setFecha(e.target.value || HOY())}
                  />
                  <Button color="light" size="sm" onClick={() => getData(fecha)} disabled={loading}>
                    {loading ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-refresh me-1"></i>}
                    Actualizar
                  </Button>
                </div>

                <div style={{ padding: "16px 22px 20px" }}>
                  {!initialized && (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  )}

                  {initialized && !data.operador && (
                    <div className="text-center text-muted py-5" style={{ fontSize: "13px" }}>
                      Tu usuario no está vinculado a un operador, por lo que no podemos mostrar tus salidas.
                      Avisa a tu analista para que active tu acceso desde el catálogo de operadores.
                    </div>
                  )}

                  {initialized && data.operador && (
                    <>
                      <SalidaEnCursoCard salida={destacada} onClick={() => destacada && abrir(destacada.id)} />

                      <div className="d-flex flex-wrap align-items-center" style={{ gap: 10, margin: "18px 0 12px" }}>
                        <div className="salidas-status-segmented">
                          {CHIPS.map((chip) => (
                            <button
                              key={chip.key}
                              type="button"
                              className={filtro === chip.key ? "active" : ""}
                              onClick={() => setFiltro(chip.key)}
                            >
                              {chip.label} <span className="opacity-75">· {data.resumen?.[chip.conteo] ?? 0}</span>
                            </button>
                          ))}
                        </div>
                        {data.resumen?.min_quemados > 0 && (
                          <span style={{ fontSize: "11.5px", fontWeight: 600, color: ESTADOS.que.ink }}>
                            {data.resumen.min_quemados}′ quemados en el día
                          </span>
                        )}
                      </div>

                      {visibles.length === 0 ? (
                        <div className="text-center text-muted py-4" style={{ fontSize: "12.5px" }}>
                          {salidas.length === 0
                            ? "No tienes salidas registradas en esta fecha."
                            : "No tienes salidas en este filtro."}
                        </div>
                      ) : (
                        visibles.map((salida) => (
                          <SalidaTile
                            key={salida.id}
                            salida={salida}
                            onClick={() => abrir(salida.id)}
                          />
                        ))
                      )}
                    </>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OperadorDashboard;
